import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, Search, Filter, GraduationCap, Mail, 
  Phone, MessageCircle, MoreVertical, Shield,
  CheckCircle2, XCircle, Info, BookOpen, User,
  FileSearch, AlertTriangle, UserCog, History,
  FileText, ExternalLink, ChevronDown, Download, Calendar
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton } from '../components/ui';
import { motion, AnimatePresence } from 'framer-motion';

export function ProfessorStudentsPage() {
  const { user } = useAuth();
  const data = useData();
  const { addToast } = useToast();
  const loading = useLoading(500);

  if (!user || user.type !== 'professor' || !data) return null;

  const { allEnrollments } = data;

  // Filtrar cursos do professor logado
  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim().toLowerCase()).filter(c => c !== '') : [];
  
  const allAvailableClasses = [
    { id: 1, name: 'Direito', course: 'Direito' },
    { id: 2, name: 'Engenharia Mecânica', course: 'Engenharia Mecânica' },
    { id: 3, name: 'Gastronomia', course: 'Gastronomia' },
    { id: 4, name: 'Medicina', course: 'Medicina' },
  ];

  const classes = allAvailableClasses.filter(c => 
    profCourses.includes(c.course.toLowerCase())
  );

  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedTurma, setSelectedTurma] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingStudent, setViewingStudent] = useState(null);
  const [chatStudent, setChatStudent] = useState(null);

  const students = (allEnrollments || []).filter(student => {
    if (!student) return false;
    
    const course = (student.course || student.courseName || '').toLowerCase();
    const fullName = (student.fullName || '').toLowerCase();
    const enrollmentId = (student.enrollmentId || '').toLowerCase();
    const turma = student.turma || '';

    const matchesClass = selectedClass === 'all' || (student.course === selectedClass || student.courseName === selectedClass);
    const matchesTurma = selectedTurma === 'all' || turma === selectedTurma;
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                          enrollmentId.includes(searchTerm.toLowerCase());
    const belongsToProf = profCourses.includes(course);
    
    return matchesClass && matchesTurma && matchesSearch && belongsToProf;
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />
      
      <div className="panelHead mb-8">
        <div>
          <h2 className="text-3xl font-black">Lista de Alunos</h2>
          <p className="text-muted font-medium">Visualize, pesquise e filtre os estudantes sob sua responsabilidade.</p>
        </div>
        <div className="flex gap-4">
          <button 
            onClick={() => window.open('https://discord.gg/lumenfall', '_blank')}
            className="flex items-center gap-3 bg-[#5865F2]/10 border border-[#5865F2]/20 px-4 py-2 rounded-2xl hover:bg-[#5865F2]/20 transition-all cursor-pointer"
          >
            <MessageCircle size={18} className="text-[#5865F2]" />
            <span className="text-sm font-bold text-[#5865F2]">Discord Docente</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div className="relative z-[100] bg-[#0a1120] border border-white/10 rounded-[2rem] p-2 shadow-2xl flex flex-col md:flex-row items-stretch gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-purple transition-colors" size={20} />
            <input 
              type="text"
              placeholder="Pesquisar por nome ou matrícula..." 
              className="w-full h-14 bg-transparent border-none rounded-3xl pl-16 pr-6 text-sm outline-none text-white font-medium placeholder:text-white/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-2 p-1.5 bg-white/[0.03] rounded-[1.5rem]">
            <FilterDropdown 
              icon={BookOpen} 
              label="Curso" 
              value={selectedClass} 
              options={[{ id: 'all', name: 'Todos os Cursos' }, ...classes]} 
              onChange={setSelectedClass}
            />

            <div className="w-px h-6 bg-white/10 hidden md:block" />

            <FilterDropdown 
              icon={Users} 
              label="Turma" 
              value={selectedTurma} 
              options={[
                { id: 'all', name: 'Todas as Turmas' },
                { id: 'A', name: 'Turma A' },
                { id: 'B', name: 'Turma B' }
              ]} 
              onChange={setSelectedTurma}
            />

            <div className="ml-1 px-5 h-10 bg-purple/10 border border-purple/20 rounded-xl flex items-center justify-center shrink-0">
              <span className="text-[9px] font-black uppercase tracking-[2px] text-purple whitespace-nowrap">
                {students.length} {students.length === 1 ? 'ALUNO' : 'ALUNOS'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48 rounded-[32px]" />)
          ) : students.length > 0 ? (
            students.map(student => (
              <StudentCard
                key={student.enrollmentId}
                student={student}
                onViewProfile={() => setViewingStudent(student)}
                onOpenChat={() => setChatStudent(student)}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-20 bg-white/5 rounded-[40px] border-2 border-dashed border-white/5">
              <Users size={64} className="mx-auto text-muted mb-6 opacity-20" />
              <h3 className="text-xl font-bold mb-2">Nenhum aluno encontrado</h3>
              <p className="text-muted max-w-sm mx-auto">
                Não existem alunos que correspondam aos filtros selecionados.
              </p>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {viewingStudent && (
          <StudentProfileModal 
            student={viewingStudent} 
            onClose={() => setViewingStudent(null)} 
            onOpenChat={(s) => {
              setViewingStudent(null);
              setChatStudent(s);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {chatStudent && (
          <ChatModal
            student={chatStudent}
            onClose={() => setChatStudent(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function FilterDropdown({ icon: Icon, label, value, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.id === value || opt.name === value) || options[0];

  return (
    <div className="relative h-12">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`h-full flex items-center gap-3 px-5 rounded-2xl transition-all hover:bg-white/5 border ${
          isOpen ? 'bg-white/10 border-purple/40 shadow-[0_0_20px_rgba(168,85,247,0.1)]' : 'border-transparent'
        }`}
      >
        <Icon size={16} className={isOpen ? 'text-purple' : 'text-muted'} />
        <div className="text-left hidden lg:block">
          <p className="text-[8px] font-black uppercase tracking-widest text-muted leading-none mb-1">{label}</p>
          <p className="text-[11px] font-bold text-white leading-none truncate max-w-[80px]">{selectedOption.name}</p>
        </div>
        <ChevronDown size={14} className={`text-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
            <motion.div 
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              className="absolute right-0 mt-3 w-64 bg-[#0d1525] border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[70] overflow-hidden backdrop-blur-xl"
            >
              <div className="p-2 max-h-64 overflow-y-auto custom-scrollbar">
                {options.map((opt) => {
                  const isSelected = opt.id === value || opt.name === value;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => {
                        onChange(opt.id === 'all' ? 'all' : opt.name);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-xl text-xs font-bold transition-all flex items-center justify-between mb-1 last:mb-0 ${
                        isSelected
                          ? 'bg-purple text-white shadow-lg shadow-purple/20'
                          : 'text-muted hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {opt.name}
                      {isSelected && <CheckCircle2 size={14} />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function StudentCard({ student, onViewProfile, onOpenChat }) {
  const [showMenu, setShowMenu] = useState(false);
  const { addToast } = useToast();
  const { calculateFrequency, grades } = useData();

  if (!student) return null;
  const fullName = student.fullName || 'Aluno Sem Nome';
  const course = student.course || student.courseName || 'N/A';
  const enrollmentId = student.enrollmentId || 'N/A';
  const turma = student.turma || 'A';

  const freq = calculateFrequency(enrollmentId, course, turma);
  
  // Calcular média geral do aluno
  const studentGrades = grades ? grades.filter(g => g.enrollmentId === enrollmentId).map(g => g.grade) : [];
  const avg = studentGrades.length > 0 
    ? (studentGrades.reduce((a, b) => a + (parseFloat(b) || 0), 0) / studentGrades.length).toFixed(1) 
    : '0.0';

  const actions = [
    { label: 'Ver Histórico', icon: History, color: 'text-blue', action: () => addToast('Abrindo Histórico Acadêmico...', 'info') },
    { label: 'Ocorrências', icon: AlertTriangle, color: 'text-yellow', action: () => addToast('Modulo de Ocorrências em breve.', 'info') },
    { label: 'Documentação', icon: FileSearch, color: 'text-purple', action: () => addToast('Verificando documentos...', 'info') },
    { label: 'Ajuste de Grade', icon: UserCog, color: 'text-green', action: () => addToast('Acesso restrito à Secretaria.', 'error') },
    { label: 'Exportar Ficha', icon: FileText, color: 'text-white', action: () => addToast('Gerando PDF do aluno...', 'success') },
  ];

  return (
    <Card className="group hover:border-purple/30 transition-all p-0 overflow-hidden relative">
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="w-14 h-14 bg-purple/10 rounded-2xl flex items-center justify-center text-purple text-xl font-black group-hover:bg-purple group-hover:text-white transition-all">
            {fullName[0]}
          </div>
          <div className="flex gap-2 relative">
            <Badge variant="blue" className="text-[10px] uppercase font-black">{course}</Badge>
            <div className="relative">
              <button 
                className={`p-2 rounded-xl transition-all ${showMenu ? 'bg-purple text-white' : 'text-muted hover:text-white hover:bg-white/5'}`}
                onClick={() => setShowMenu(!showMenu)}
              >
                <MoreVertical size={20} />
              </button>

              <AnimatePresence>
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-2 w-56 bg-[#0b1322] border border-white/10 rounded-2xl shadow-2xl z-20 overflow-hidden"
                    >
                      <div className="p-2 border-b border-white/5 bg-white/5">
                        <span className="text-[9px] uppercase font-black text-muted tracking-widest px-3">Gerenciamento</span>
                      </div>
                      <div className="p-1">
                        {actions.map((item, idx) => (
                          <button 
                            key={idx}
                            className="w-full flex items-center gap-3 px-4 py-3 text-xs font-bold text-muted hover:text-white hover:bg-white/5 transition-all rounded-xl"
                            onClick={() => {
                              item.action();
                              setShowMenu(false);
                            }}
                          >
                            <item.icon size={16} className={item.color} />
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <h3 className="text-lg font-bold mb-1 truncate">{fullName}</h3>
        <div className="flex items-center gap-2 text-muted text-xs font-mono mb-6">
          <Shield size={12} className="text-purple" />
          {enrollmentId} • Turma {turma}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase font-black text-muted tracking-widest">Frequência</span>
            <strong className={`${freq >= 75 ? 'text-green' : 'text-red'} text-sm`}>{freq}%</strong>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] uppercase font-black text-muted tracking-widest">Média Geral</span>
            <strong className="text-white text-sm">{avg}</strong>
          </div>
        </div>
      </div>

      <div className="flex border-t border-white/5">
        <button 
          className="flex-1 py-4 flex items-center justify-center gap-2 text-xs font-bold hover:bg-white/5 transition-all border-r border-white/5"
          onClick={onOpenChat}
        >
          <MessageCircle size={16} className="text-purple" />
          Mensagem
        </button>
        <button 
          className="flex-1 py-4 flex items-center justify-center gap-2 text-xs font-bold hover:bg-white/5 transition-all"
          onClick={onViewProfile}
        >
          <User size={16} className="text-purple" />
          Ver Perfil
        </button>
      </div>
    </Card>
  );
}

function StudentProfileModal({ student, onClose, onOpenChat }) {
  const { calculateFrequency, grades, allEnrollments, idPhotos } = useData();
  
  if (!student) return null;

  const fullName = student.fullName || 'Aluno Sem Nome';
  const course = student.course || student.courseName || 'N/A';
  const enrollmentId = student.enrollmentId || 'N/A';
  const turma = student.turma || 'A';
  const latestStudentRecord = (allEnrollments || []).find(e => e?.enrollmentId === student?.enrollmentId);
  const studentPhoto = (idPhotos && idPhotos[student?.enrollmentId]) || latestStudentRecord?.profilePicture || student?.profilePicture || null;
  
  const freq = calculateFrequency(enrollmentId, course, turma);
  const studentGrades = grades ? grades.filter(g => g.enrollmentId === enrollmentId) : [];
  const avg = studentGrades.length > 0 
    ? (studentGrades.reduce((a, b) => a + (parseFloat(b.grade) || 0), 0) / studentGrades.length).toFixed(1) 
    : '0.0';

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 2147483647, padding: 16 }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(3, 7, 18, 0.92)',
          backdropFilter: 'blur(18px)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 24 }}
        style={{
          position: 'relative',
          width: 'min(1100px, calc(100% - 32px))',
          height: 'min(820px, 90vh)',
          backgroundColor: '#0a1120',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 40,
          overflow: 'hidden',
          boxShadow: '0 40px 120px rgba(0,0,0,0.65)',
          display: 'flex',
        }}
      >
        <div
          style={{
            width: 360,
            flexShrink: 0,
            backgroundColor: 'rgba(13, 21, 37, 0.95)',
            borderRight: '1px solid rgba(255,255,255,0.06)',
            padding: 24,
            overflow: 'auto',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--purple)' }}>
              Carteirinha
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>
              Visualização apenas
            </div>
          </div>

          <div
            style={{
              width: 260,
              margin: '0 auto',
              borderRadius: 28,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
              backgroundColor: '#0a1120',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(139,92,246,0.18), rgba(0,0,0,0.85))',
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', padding: 18, textAlign: 'center' }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                }}
              >
                <GraduationCap size={18} color="#000" />
              </div>
              <div style={{ fontWeight: 900, letterSpacing: 4, fontSize: 12, color: '#fff' }}>LUMENFALL</div>
            </div>

            <div style={{ position: 'relative', padding: '0 18px 18px', textAlign: 'center' }}>
              <div
                style={{
                  width: 112,
                  height: 112,
                  borderRadius: 18,
                  border: '2px solid rgba(255,255,255,0.10)',
                  padding: 6,
                  background: '#030712',
                  margin: '0 auto 14px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 12,
                    background: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 36,
                    fontWeight: 900,
                    color: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  {studentPhoto ? (
                    <img src={studentPhoto} alt="Foto do aluno" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    fullName[0]
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 8, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--purple)' }}>
                  Nome Completo
                </div>
                <div style={{ fontSize: 12, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>{fullName}</div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 0,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  background: 'rgba(255,255,255,0.03)',
                  padding: '10px 0',
                  marginBottom: 12,
                }}
              >
                <div style={{ borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 7, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>
                    Matrícula
                  </div>
                  <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas', fontSize: 10, fontWeight: 900, color: '#fff' }}>
                    {enrollmentId}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 7, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>
                    Turma
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 900, color: '#fff' }}>{turma}</div>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 7, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>
                  Curso
                </div>
                <div style={{ fontSize: 11, fontWeight: 900, color: 'var(--purple)', textTransform: 'uppercase' }}>{course}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ background: '#fff', padding: 8, borderRadius: 10 }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=70x70&data=${enrollmentId}&color=000000&bgcolor=ffffff`}
                    alt="QR"
                    style={{ width: 56, height: 56 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: 28, overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Badge variant="purple">Estudante</Badge>
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                  {enrollmentId}
                </span>
              </div>
              <h2 style={{ margin: 0, fontSize: 34, fontWeight: 900, letterSpacing: -1, textTransform: 'uppercase' }}>{fullName}</h2>
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                {course} • Turma {turma}
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                width: 44,
                height: 44,
                borderRadius: 16,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.04)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <XCircle size={22} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 24, padding: 18, background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                Frequência
              </div>
              <div style={{ marginTop: 10, fontSize: 34, fontWeight: 900, color: freq >= 75 ? 'var(--green)' : 'var(--red)' }}>
                {freq}%
              </div>
            </div>
            <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 24, padding: 18, background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                Média Geral
              </div>
              <div style={{ marginTop: 10, fontSize: 34, fontWeight: 900, color: '#fff' }}>
                {avg}
              </div>
            </div>
          </div>

          <div style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 28, padding: 18, background: 'rgba(255,255,255,0.02)' }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
              Lançamentos Recentes
            </div>
            {studentGrades.length > 0 ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {studentGrades.slice(0, 3).map((g, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 14px',
                      borderRadius: 18,
                      border: '1px solid rgba(255,255,255,0.06)',
                      background: 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', color: 'rgba(255,255,255,0.80)' }}>
                        {g.subject}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
                        {new Date(g.date || Date.now()).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--purple)' }}>{g.grade}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 16, borderRadius: 18, border: '1px dashed rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.35)', fontWeight: 800 }}>
                Nenhum lançamento registrado.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
            <Button
              className="flex-1"
              onClick={() => {
                onOpenChat(student);
              }}
            >
              <Mail size={18} className="mr-2" /> Contatar Estudante
            </Button>
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

function ChatModal({ student, onClose }) {
  const { user } = useAuth();
  const { getConversationId, getConversationMessages, sendChatMessage } = useData();
  const [text, setText] = useState('');

  if (!student) return null;

  const studentName = student.fullName || 'Estudante';
  const studentId = student.enrollmentId || student.citizenId; // Usar ID persistente
  const fromId = user.enrollmentId; // Professor logado
  const conversationId = getConversationId(fromId, studentId);
  const messages = getConversationMessages(conversationId);

  const handleSend = () => {
    const clean = String(text || '').trim();
    if (!clean) return;
    sendChatMessage({ conversationId, fromId, toId: studentId, text: clean });
    setText('');
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 2147483647, padding: 16 }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(3, 7, 18, 0.92)',
          backdropFilter: 'blur(18px)',
        }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 24 }}
        style={{
          position: 'relative',
          width: 'min(900px, calc(100% - 32px))',
          height: 'min(640px, 85vh)',
          backgroundColor: '#0a1120',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 28px 90px rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: 'rgba(139,92,246,0.15)',
              border: '1px solid rgba(139,92,246,0.25)',
              color: 'var(--purple)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 900,
              flexShrink: 0
            }}>
              {(studentName || 'E')[0]}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {studentName}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.40)' }}>
                Chat acadêmico
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 40,
              height: 40,
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.04)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <XCircle size={22} />
          </button>
        </div>

        <div style={{ flex: 1, padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.length === 0 ? (
            <div style={{ padding: 14, borderRadius: 14, border: '1px dashed rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)', fontWeight: 700 }}>
              Nenhuma mensagem ainda. Envie a primeira mensagem.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.fromId === fromId;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '10px 12px',
                      borderRadius: 16,
                      background: mine ? 'rgba(139,92,246,0.90)' : 'rgba(255,255,255,0.06)',
                      border: mine ? '1px solid rgba(139,92,246,0.25)' : '1px solid rgba(255,255,255,0.10)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: 1.35,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 10 }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Digite sua mensagem..."
            style={{
              flex: 1,
              height: 44,
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.03)',
              color: '#fff',
              padding: '0 14px',
              outline: 'none',
              fontWeight: 600,
            }}
          />
          <button
            onClick={handleSend}
            style={{
              height: 44,
              padding: '0 16px',
              borderRadius: 14,
              border: '0',
              background: 'rgba(139,92,246,0.95)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
            }}
          >
            Enviar
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

function ProfileItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-white/[0.02] rounded-2xl border border-white/5">
      <div className="w-10 h-10 bg-purple/10 rounded-xl flex items-center justify-center text-purple">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[8px] font-black uppercase tracking-widest text-muted mb-0.5">{label}</p>
        <p className="text-sm font-bold text-white">{value}</p>
      </div>
    </div>
  );
}
